import { NamespaceSchema, ResolveOptions, resolveMessages } from "./catalogue";
import {
  assertPluralRulesAvailable,
  formatMessage,
  MessageArgs,
} from "./format";
import { COPY_SCHEMAS, CopyNamespace, CopyTypes } from "./generated";
import { BUNDLES } from "./locales";

export type CopyOf<N extends CopyNamespace> = CopyTypes[N];

interface CopyTree {
  [segment: string]: CopyTree | string | ((args: MessageArgs) => string);
}

const buildTree = (
  schema: NamespaceSchema,
  messages: Readonly<Record<string, string>>,
  locale: string
): CopyTree => {
  const root: CopyTree = {};
  for (const key of Object.keys(schema.keys)) {
    // A missing message renders its key rather than nothing, so a gap is visible.
    const message = messages[key] ?? key;
    const segments = key.split(".");
    let node = root;
    for (const segment of segments.slice(0, -1)) {
      const next = node[segment];
      if (typeof next === "object") {
        node = next;
      } else {
        const created: CopyTree = {};
        node[segment] = created;
        node = created;
      }
    }
    const leaf = segments[segments.length - 1];
    node[leaf] =
      schema.keys[key].length > 0
        ? (args: MessageArgs) => formatMessage(message, locale, args)
        : formatMessage(message, locale);
  }
  return root;
};

/**
 * Resolves one namespace for a locale and product kind and returns the typed
 * accessor object the generated interface describes. Bind it once where the
 * product is known (a flow root, a request handler) and pass it down.
 */
export const createCopy = <N extends CopyNamespace>(
  namespace: N,
  options: ResolveOptions
): CopyOf<N> => {
  assertPluralRulesAvailable();
  const messages = resolveMessages(BUNDLES[namespace], options);
  return buildTree(
    COPY_SCHEMAS[namespace],
    messages,
    options.locale
  ) as unknown as CopyOf<N>;
};
